import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Sparkles, Mic, RefreshCw, User, Check, RotateCcw } from "lucide-react";
import { motion } from "framer-motion";
import { AddTaskDialog, TaskDetailDialog } from "@/components/EntityDialogs";
import { colorMeta } from "@/lib/colors";

const columns = [
  { key: "todo", label: "To do" },
  { key: "in-progress", label: "In progress" },
  { key: "done", label: "Done" },
];

const sourceIcon = { ai: Sparkles, voice: Mic, renewal: RefreshCw, manual: User };

export default function Tasks() {
  const { openVoice } = useOutletContext();
  const { tasks, toggleTask, setTaskStatus, taskCategories } = useFgp();
  const [view, setView] = useState("board");
  const [addOpen, setAddOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const detailTask = tasks.find((t) => t.id === detailId) || null;
  const catOf = (id) => taskCategories.find((c) => c.id === id);

  const overdue = tasks.filter((t) => t.status === "overdue");
  const visible = tasks.filter((t) => catFilter === "all" || t.category === catFilter);

  const TaskCard = ({ t }) => {
    const cat = catOf(t.category);
    const cm = colorMeta(cat?.color);
    return (
      <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        onClick={() => setDetailId(t.id)}
        className={`bg-white rounded-lg border border-slate-200 border-l-4 ${cm.leftBorder} p-3.5 shadow-sm cursor-pointer hover:border-slate-300 hover:shadow-md transition-all`} data-testid={`task-${t.id}`}>
        <div className="min-w-0">
          <p className={`text-sm font-medium ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.title}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {cat && <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cm.chip}`}><span className={`h-1.5 w-1.5 rounded-full ${cm.dot}`} /> {cat.name}</span>}
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${t.priority === "high" ? "bg-rose-50 text-rose-600" : t.priority === "medium" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>{t.priority}</span>
            <span className="text-xs text-slate-400">{t.person}</span>
            {t.source === "ai" && <span className="inline-flex items-center gap-0.5 text-[10px] text-neutral-600 bg-neutral-50 px-1.5 py-0.5 rounded-full"><Sparkles className="h-2.5 w-2.5" /> AI</span>}
            {t.source === "voice" && <span className="inline-flex items-center gap-0.5 text-[10px] text-neutral-600 bg-neutral-50 px-1.5 py-0.5 rounded-full"><Mic className="h-2.5 w-2.5" /> Voice</span>}
          </div>
          <p className="text-xs text-slate-400 mt-2">Due {t.due} · {t.assignee}</p>
          {t.status === "done" ? (
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setTaskStatus(t.id, "todo"); }} className="w-full mt-3 gap-1.5 text-slate-500" data-testid={`task-reopen-${t.id}`}>
              <RotateCcw className="h-3.5 w-3.5" /> Reopen
            </Button>
          ) : (
            <Button size="sm" onClick={(e) => { e.stopPropagation(); setTaskStatus(t.id, "done"); }} className="w-full mt-3 gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid={`task-done-${t.id}`}>
              <Check className="h-4 w-4" /> Mark done
            </Button>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div>
      <PageHeader
        title="Tasks"
        subtitle="Everything the household team needs to action — manual, renewal-driven and AI-suggested."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={openVoice} className="gap-2" data-testid="tasks-voice"><Mic className="h-4 w-4" /> Voice</Button>
            <Button onClick={() => setAddOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="add-task"><Plus className="h-4 w-4" /> Add task</Button>
          </div>
        }
      />

      {overdue.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="overdue-banner">
          <span className="h-8 w-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center"><RefreshCw className="h-4 w-4" /></span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-800">{overdue.length} overdue task{overdue.length > 1 ? "s" : ""}</p>
            <p className="text-xs text-rose-600">{overdue.map((t) => t.title).join(" · ")}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex gap-1.5">
          {["board", "list"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${view === v ? "bg-neutral-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{v}</button>
          ))}
        </div>
        <div className="h-5 w-px bg-slate-200 hidden sm:block" />
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setCatFilter("all")} data-testid="task-cat-filter-all"
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${catFilter === "all" ? "bg-neutral-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>All</button>
          {taskCategories.map((c) => {
            const cm = colorMeta(c.color);
            const active = catFilter === c.id;
            return (
              <button key={c.id} onClick={() => setCatFilter(c.id)} data-testid={`task-cat-filter-${c.id}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${active ? cm.chip + " ring-1 ring-inset ring-current" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                <span className={`h-2 w-2 rounded-full ${cm.dot}`} /> {c.name}
              </button>
            );
          })}
        </div>
      </div>

      {view === "board" ? (
        <div className="grid md:grid-cols-3 gap-4">
          {columns.map((col) => {
            const colTasks = visible.filter((t) => col.key === "todo" ? (t.status === "todo" || t.status === "overdue") : t.status === col.key);
            return (
              <div key={col.key} className="bg-slate-100/60 rounded-xl p-3" data-testid={`column-${col.key}`}>
                <div className="flex items-center justify-between px-1 mb-3">
                  <h3 className="text-sm font-semibold text-slate-700">{col.label}</h3>
                  <span className="text-xs font-medium text-slate-400 bg-white px-2 py-0.5 rounded-full">{colTasks.length}</span>
                </div>
                <div className="space-y-2.5">
                  {colTasks.map((t) => <TaskCard key={t.id} t={t} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {visible.map((t) => {
            const cat = catOf(t.category);
            const cm = colorMeta(cat?.color);
            return (
              <div key={t.id} onClick={() => setDetailId(t.id)} className={`flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-slate-50 transition-colors border-l-4 ${cm.leftBorder}`} data-testid={`task-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.title}</p>
                  <p className="text-xs text-slate-400">{t.person} · Due {t.due}</p>
                </div>
                {cat && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full hidden sm:inline ${cm.chip}`}>{cat.name}</span>}
                <StatusBadge status={t.priority} />
                {t.status === "done" ? (
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setTaskStatus(t.id, "todo"); }} className="gap-1.5 text-slate-500" data-testid={`task-row-reopen-${t.id}`}><RotateCcw className="h-3.5 w-3.5" /> Reopen</Button>
                ) : (
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); setTaskStatus(t.id, "done"); }} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid={`task-row-done-${t.id}`}><Check className="h-4 w-4" /> Done</Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AddTaskDialog open={addOpen} onOpenChange={setAddOpen} />
      <TaskDetailDialog open={detailId !== null} onOpenChange={(o) => { if (!o) setDetailId(null); }} task={detailTask} />
    </div>
  );
}
